import React, { useEffect, useState } from "react";
import {
    ShieldCheck,
    ArrowRight,
    CalendarClock,
    Clock,
    BookOpen,
    Users,
    Lock,
    Building2,
    CheckCircle2,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    Home,
    PlayCircle,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Login = () => {
    const handleGoogleLogin = () => {
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
            {/* =========================================
                LEFT DARK PANEL — hero, preview, modules
                ========================================= */}
            <div className="relative hidden lg:flex flex-col justify-between bg-[#0B1117] text-slate-200 p-12 xl:p-14 overflow-hidden">
                {/* Ambient teal glow */}
                <div aria-hidden="true" className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#2EB4AC] opacity-[0.10] blur-3xl pointer-events-none" />
                <div aria-hidden="true" className="absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-[#2EB4AC] opacity-[0.06] blur-3xl pointer-events-none" />
                {/* Dot grid */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{ backgroundImage: "radial-gradient(#2EB4AC 1px, transparent 1px)", backgroundSize: "22px 22px" }}
                />

                {/* Logo + name */}
                <div className="relative z-10 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-[#2EB4AC] text-[#0B1117] flex items-center justify-center font-heading font-bold text-2xl shadow-lg shadow-[#2EB4AC]/20">
                        O
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-white text-[26px] tracking-tight">Odyssey</div>
                        <div className="text-[11px] text-slate-400 tracking-[0.15em] uppercase">by Wellversed</div>
                    </div>
                </div>

                {/* Hero copy + preview card */}
                <div className="relative z-10 mt-12 xl:mt-10">
                    <h1 className="font-heading text-[44px] xl:text-[54px] font-bold text-white tracking-tight leading-[1.05]">
                        From attendance<br />to alignment.<br />
                        <span className="text-[#2EB4AC]">From learning to leadership.</span>
                    </h1>
                    <p className="mt-5 text-slate-400 text-[15px] leading-relaxed max-w-md">
                        A single workspace for the rhythms of your workday — calm, considered, and built to respect your time.
                    </p>

                    {/* Mini-preview card — animated slides */}
                    <PreviewSlider />
                </div>

                {/* Bottom feature chips */}
                <div className="relative z-10 mt-12 flex flex-wrap gap-2 max-w-2xl">
                    <Chip icon={CalendarClock} label="PAS Sessions" />
                    <Chip icon={Clock} label="Attendance · WFH · OFF" />
                    <Chip icon={BookOpen} label="Learning Hub" />
                    <Chip icon={Users} label="Role-aware views" />
                    <Chip icon={Building2} label="Department grouping" />
                </div>

                <div className="relative z-10 mt-8 text-[11px] text-slate-500 tracking-wider">
                    © {new Date().getFullYear()} Wellversed · Odyssey internal dashboard
                </div>
            </div>

            {/* =========================================
                RIGHT LIGHT PANEL — sign in
                ========================================= */}
            <div className="relative flex flex-col items-center justify-center p-8 sm:p-12 bg-white">
                <div className="w-full max-w-sm">
                    {/* Mobile brand */}
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <div className="h-10 w-10 rounded-lg bg-[#2EB4AC] text-white flex items-center justify-center font-heading font-bold text-lg">O</div>
                        <div className="leading-tight">
                            <div className="font-heading font-bold text-slate-900 text-2xl tracking-tight">Odyssey</div>
                            <div className="text-[10px] text-slate-500 tracking-wide uppercase">by Wellversed</div>
                        </div>
                    </div>

                    {/* Emblem */}
                    <div className="flex flex-col items-center text-center">
                        <div className="relative h-24 w-24 flex items-center justify-center">
                            {/* Outer halo glow */}
                            <div className="absolute inset-2 rounded-2xl bg-[#2EB4AC]/30 blur-xl animate-pulse" aria-hidden="true" />
                            {/* Rotating dashed ring */}
                            <svg
                                className="odyssey-emblem-ring absolute inset-0 h-full w-full"
                                viewBox="0 0 100 100"
                                fill="none"
                                aria-hidden="true"
                            >
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="46"
                                    stroke="#2EB4AC"
                                    strokeWidth="1.5"
                                    strokeDasharray="3 6"
                                    opacity="0.7"
                                />
                            </svg>
                            {/* Badge */}
                            <div className="odyssey-emblem relative h-16 w-16 rounded-2xl bg-[#2EB4AC] text-white flex items-center justify-center font-heading font-bold text-3xl shadow-lg shadow-[#2EB4AC]/30">
                                O
                            </div>
                        </div>

                        <h2 className="mt-8 font-heading text-[34px] font-semibold text-slate-900 tracking-tight">Welcome back</h2>
                        <p className="mt-2 text-slate-500 text-[15px]">
                            Sign in to pick up where you left off.
                        </p>
                    </div>

                    {/* Restricted access pill card */}
                    <div className="mt-8 bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex items-center gap-3" data-testid="restricted-access-card">
                        <div className="h-10 w-10 shrink-0 rounded-xl bg-[#2EB4AC]/10 border border-[#2EB4AC]/25 text-[#25908A] flex items-center justify-center">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading font-semibold text-slate-900 text-[14px]">Restricted Access</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                Only <span className="font-mono text-slate-700">@wellversed.in</span> emails
                            </div>
                        </div>
                    </div>

                    {/* Primary CTA */}
                    <button
                        onClick={handleGoogleLogin}
                        data-testid="google-login-button"
                        className="mt-4 w-full h-12 inline-flex items-center justify-center gap-3 bg-[#0B1117] hover:bg-[#131921] text-white rounded-xl font-medium text-[15px] transition-all shadow-lg shadow-slate-900/10 group"
                    >
                        <GoogleIcon />
                        Continue with Google
                        <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </button>

                    {/* Secure SSO divider */}
                    <div className="mt-8 flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Secure SSO</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <div className="mt-4 flex items-center justify-center gap-6 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[#2EB4AC]" />
                            SSL Secured
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Lock className="h-3.5 w-3.5 text-[#2EB4AC]" />
                            Domain Restricted
                        </span>
                    </div>

                    <p className="mt-8 text-center text-[11px] text-slate-400">
                        By signing in, you agree to use Odyssey with your company credentials.
                    </p>
                </div>
            </div>
        </div>
    );
};

// ============ Helpers (below) ============

const SLIDES = [
    { id: "pas", label: "PAS Sessions", icon: CalendarClock },
    { id: "attendance", label: "Attendance", icon: Clock },
    { id: "learning", label: "Learning Hub", icon: BookOpen },
];
const AUTO_ROTATE_MS = 4200;

const PreviewSlider = () => {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), AUTO_ROTATE_MS);
        return () => clearInterval(t);
    }, [paused]);

    const ActiveIcon = SLIDES[idx].icon;

    return (
        <div
            className="mt-10 relative rounded-2xl bg-gradient-to-br from-[#131921] to-[#0F151C] border border-white/[0.06] shadow-2xl p-5 max-w-xl overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Top row — mock app chrome */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-red-400/60" />
                    <span className="h-2 w-2 rounded-full bg-yellow-400/60" />
                    <span className="h-2 w-2 rounded-full bg-green-400/60" />
                </div>
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <ActiveIcon className="h-3 w-3 text-[#2EB4AC]" />
                    {SLIDES[idx].label} · Live
                </div>
            </div>

            {/* Slides — cross-fade */}
            <div className="relative" style={{ minHeight: 248 }}>
                {SLIDES.map((s, i) => (
                    <div
                        key={s.id}
                        aria-hidden={i !== idx}
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
                    >
                        {s.id === "pas" && <PasSlide />}
                        {s.id === "attendance" && <AttendanceSlide />}
                        {s.id === "learning" && <LearningSlide />}
                    </div>
                ))}
            </div>

            {/* Pagination */}
            <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    {SLIDES.map((s, i) => (
                        <button
                            key={s.id}
                            onClick={() => setIdx(i)}
                            aria-label={`Show ${s.label}`}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx ? "w-6 bg-[#2EB4AC]" : "w-1.5 bg-white/20 hover:bg-white/40"
                            }`}
                        />
                    ))}
                </div>
                <div className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                    <PlayCircle className={`h-3 w-3 ${paused ? "text-slate-600" : "text-[#2EB4AC]"}`} />
                    {paused ? "Paused" : "Auto"}
                </div>
            </div>
        </div>
    );
};

const PasSlide = () => (
    <>
        <div className="grid grid-cols-2 gap-3">
            <MiniStat icon={CheckCircle2} label="Conducted" value="128" delta="+14%" color="emerald" />
            <MiniStat icon={AlertTriangle} label="Overdue" value="3" delta="−50%" color="teal" inverted />
        </div>
        <div className="mt-3 rounded-xl bg-[#0B1117] border border-white/[0.05] p-4">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">On-time completion · 6 mo</div>
                <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400">
                    <TrendingUp className="h-3 w-3" /> +12.3%
                </div>
            </div>
            <svg viewBox="0 0 300 60" className="w-full h-14" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="pasGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d="M0,45 L30,42 L60,38 L90,40 L120,30 L150,33 L180,22 L210,18 L240,24 L270,12 L300,16 L300,60 L0,60 Z" fill="url(#pasGrad)" />
                <path d="M0,45 L30,42 L60,38 L90,40 L120,30 L150,33 L180,22 L210,18 L240,24 L270,12 L300,16" fill="none" stroke="#2EB4AC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="300" cy="16" r="3" fill="#0B1117" stroke="#2EB4AC" strokeWidth="1.5" />
            </svg>
            <div className="mt-1 flex items-center justify-between text-[9px] text-slate-600 uppercase tracking-wider">
                <span>Aug</span><span>Sep</span><span>Oct</span><span>Nov</span><span>Dec</span><span>Jan</span>
            </div>
        </div>
    </>
);

const AttendanceSlide = () => (
    <>
        <div className="grid grid-cols-3 gap-3">
            <MiniStat icon={TrendingUp} label="Late entries" value="47" delta="−8%" color="teal" inverted />
            <MiniStat icon={Home} label="WFH days" value="62" color="emerald" />
            <MiniStat icon={TrendingDown} label="Early exits" value="12" delta="−18%" color="teal" inverted />
        </div>
        <div className="mt-3 rounded-xl bg-[#0B1117] border border-white/[0.05] p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Severity · Dept × Month</div>
                <div className="text-[10px] text-slate-500">6 teams · Nov '25</div>
            </div>
            <div className="grid grid-cols-6 gap-1.5">
                {[0.18, 0.42, 0.65, 0.28, 0.55, 0.75,
                  0.22, 0.3, 0.48, 0.62, 0.88, 0.4,
                  0.12, 0.55, 0.32, 0.78, 0.45, 0.6].map((v, i) => (
                    <div key={i} className="h-5 rounded-[3px]" style={{ backgroundColor: `rgba(46, 180, 172, ${v.toFixed(2)})` }} />
                ))}
            </div>
            <div className="mt-2 flex items-center justify-between text-[9px] text-slate-600 uppercase tracking-wider">
                <span>Low</span><span>Med</span><span>High</span>
            </div>
        </div>
    </>
);

const LearningSlide = () => (
    <>
        <div className="grid grid-cols-2 gap-3">
            <MiniStat icon={CheckCircle2} label="Completed" value="34" delta="+7" color="emerald" />
            <MiniStat icon={BookOpen} label="Assigned" value="52" color="teal" />
        </div>
        <div className="mt-3 rounded-xl bg-[#0B1117] border border-white/[0.05] p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Collections · Progress</div>
                <div className="text-[10px] text-emerald-400 font-semibold">65%</div>
            </div>
            {[
                { name: "Leadership Essentials", pct: 82 },
                { name: "Product Strategy", pct: 54 },
                { name: "Comms & Writing", pct: 38 },
            ].map((c) => (
                <div key={c.name}>
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-slate-300">{c.name}</span>
                        <span className="text-[10px] text-slate-500 tabular-nums">{c.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-[#2EB4AC] to-[#3BC5BC] transition-all duration-1000"
                            style={{ width: `${c.pct}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    </>
);

const MiniStat = ({ icon: Icon, label, value, delta, color, inverted }) => {
    const dot = color === "emerald" ? "bg-emerald-400" : "bg-[#2EB4AC]";
    const deltaColor = (delta?.startsWith("+") && !inverted) || (delta?.startsWith("−") && inverted)
        ? "text-emerald-400"
        : "text-red-400";
    return (
        <div className="rounded-xl bg-[#0B1117] border border-white/[0.05] p-3">
            <div className="flex items-center gap-1.5 mb-1">
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</span>
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-white tabular-nums">{value}</span>
                {delta && <span className={`text-[10px] font-semibold ${deltaColor}`}>{delta}</span>}
            </div>
            <Icon className="h-3.5 w-3.5 text-slate-600 mt-1" />
        </div>
    );
};

const HeatCell = ({ intensity }) => {
    const alpha = Math.max(0.08, Math.min(0.85, intensity));
    return (
        <div className="h-8 rounded-md" style={{ backgroundColor: `rgba(46, 180, 172, ${alpha.toFixed(2)})` }} />
    );
};

const Chip = ({ icon: Icon, label }) => (
    <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-white/[0.04] border border-white/[0.08] text-[11px] font-medium text-slate-300 backdrop-blur-sm">
        <Icon className="h-3 w-3 text-[#2EB4AC]" />
        {label}
    </span>
);

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.3 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41.6 35.1 44 30 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
);

export default Login;
